import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { X, Upload, DollarSign, AlertCircle, CheckCircle, Wallet } from 'lucide-react';
import { RootState } from '../../store';
import { 
  useCreateInvestmentMutation, 
  useCreateWalletInvestmentMutation,
  useGetUserWalletBalanceQuery 
} from '../../store/slices/apiSlice';
import { addNotification } from '../../store/slices/notificationSlice';
import toast from 'react-hot-toast';

const schema = yup.object({
  amount: yup
    .number()
    .required('Investment amount is required')
    .min(1, 'Amount must be greater than 0'),
  paymentMethod: yup
    .string()
    .oneOf(['wallet', 'screenshot'], 'Please select a payment method')
    .required('Payment method is required'),
  paymentProof: yup
    .mixed<File>()
    .when('paymentMethod', {
      is: 'screenshot',
      then: (schema) => schema
    .required('Payment proof is required')
    .test('fileSize', 'File size must be less than 10MB', (value) => {
      if (!value) return true;
      return (value as File).size <= 10 * 1024 * 1024;
    })
    .test('fileType', 'Only image files are allowed', (value) => {
      if (!value) return true;
      return (value as File).type.startsWith('image/');
        }),
      otherwise: (schema) => schema.notRequired(),
    }),
});

type InvestmentFormData = yup.InferType<typeof schema>;

interface InvestmentModalProps {
  project: any;
  onClose: () => void;
  onSuccess?: () => void;
}

const InvestmentModal: React.FC<InvestmentModalProps> = ({ project, onClose, onSuccess }) => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [createInvestment, { isLoading: isCreatingInvestment }] = useCreateInvestmentMutation();
  const [createWalletInvestment, { isLoading: isCreatingWalletInvestment }] = useCreateWalletInvestmentMutation();
  const { data: walletBalanceData, refetch: refetchWallet } = useGetUserWalletBalanceQuery(undefined, {
    skip: !user,
  });
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const dispatch = useDispatch();
  
  const isLoading = isCreatingInvestment || isCreatingWalletInvestment;
  const walletBalance = walletBalanceData?.data?.wallet_balance || 0;
  const currencySymbol = walletBalanceData?.data?.currency_symbol || project?.currency_symbol || '$';

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<InvestmentFormData>({
    resolver: yupResolver(schema),
  });

  const watchedAmount = watch('amount');
  const watchedPaymentMethod = watch('paymentMethod');

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file size
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Only image files are allowed');
        return;
      }
      setSelectedFile(file);
      setValue('paymentProof', file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Calculate expected ROI based on backend formula
  const calculateExpectedROI = (amountInvested: number, roiPercentage: number, durationMonths: number): number => {
    if (!amountInvested || !roiPercentage || !durationMonths) return 0;
    const annualROI = (amountInvested * roiPercentage) / 100;
    const proratedROI = (annualROI * durationMonths) / 12;
    return Math.round(proratedROI * 100) / 100;
  };

  const onSubmit = async (data: InvestmentFormData) => {
    if (!project?.project_id) {
      toast.error('Invalid project information');
      return;
    }

    // Validate payment method specific requirements
    if (data.paymentMethod === 'screenshot' && !selectedFile) {
      toast.error('Please select a payment proof file');
      return;
    }

    if (data.paymentMethod === 'wallet' && walletBalance < data.amount) {
      toast.error('Insufficient wallet balance');
      return;
    }

    try {
      let result;
      
      if (data.paymentMethod === 'wallet') {
        result = await createWalletInvestment({
          project_id: project.project_id,
          amount_invested: data.amount,
        }).unwrap();
        
        // Refetch wallet balance after successful investment
        await refetchWallet();
      } else {
        if (!selectedFile) {
          toast.error('Please select a payment proof file');
          return;
        }
        result = await createInvestment({
        project_id: project.project_id,
        amount_invested: data.amount,
        file: selectedFile,
      }).unwrap();
      }

      toast.success(result.message || 'Investment submitted successfully!');
      
      dispatch(addNotification({
        title: 'Investment Submitted',
        message: result.message || (data.paymentMethod === 'wallet' 
          ? 'Your investment has been approved and processed from your wallet.' 
          : 'Your investment has been submitted for review. You will be notified once it\'s approved.'),
        type: 'success',
      }));

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.message || 'There was an error submitting your investment. Please try again.';
      toast.error(errorMessage);
      dispatch(addNotification({
        title: 'Investment Failed',
        message: errorMessage,
        type: 'error',
      }));
    }
  };

  const formatCurrency = (amount: number | string, currencySymbol: string = '$') => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return `${currencySymbol}0.00`;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numAmount).replace('$', currencySymbol);
  };

  const fundingGoal = parseFloat(project?.funding_goal || '0');
  const amountRaised = parseFloat(project?.amount_raised || '0');
  const remainingAmount = fundingGoal - amountRaised;
  const minInvestment = parseFloat(project?.rules?.min_allowed_investment || '1000');
  const maxInvestment = project?.rules?.max_allowed_investment 
    ? parseFloat(project.rules.max_allowed_investment) 
    : Math.min(remainingAmount, 1000000);
  const expectedROIPercentage = parseFloat(project?.expected_roi_percentage || '0');
  const roiDuration = project?.roi_duration_months || 12;
  
  // Calculate expected ROI for the watched amount
  const expectedROIAmount = watchedAmount 
    ? calculateExpectedROI(watchedAmount, expectedROIPercentage, roiDuration)
    : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">Invest in {project.title}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {/* Hidden input for payment method */}
          <input type="hidden" {...register('paymentMethod')} />
          
          {/* Project Summary */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-2">Investment Summary</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Minimum Investment</p>
                <p className="font-bold text-gray-900">{formatCurrency(minInvestment, currencySymbol)}</p>
              </div>
              <div>
                <p className="text-gray-600">Expected ROI</p>
                <p className="font-bold text-success-600">{expectedROIPercentage}%</p>
              </div>
              <div>
                <p className="text-gray-600">Remaining Amount</p>
                <p className="font-bold text-gray-900">{formatCurrency(remainingAmount, currencySymbol)}</p>
              </div>
              <div>
                <p className="text-gray-600">Project Duration</p>
                <p className="font-bold text-gray-900">{roiDuration} months</p>
              </div>
            </div>
          </div>

          {/* Investment Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Investment Amount
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="number"
                {...register('amount')}
                min={minInvestment}
                max={maxInvestment}
                step="1000"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder={`Minimum ${formatCurrency(minInvestment, currencySymbol)}`}
              />
            </div>
            {errors.amount && (
              <p className="text-danger-500 text-sm mt-1">{errors.amount.message}</p>
            )}
            {watchedAmount && watchedAmount < minInvestment && (
              <p className="text-warning-600 text-sm mt-1 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                Minimum investment is {formatCurrency(minInvestment, currencySymbol)}
              </p>
            )}
            {watchedAmount && watchedAmount > maxInvestment && (
              <p className="text-warning-600 text-sm mt-1 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                Maximum investment is {formatCurrency(maxInvestment, currencySymbol)}
              </p>
            )}
            {watchedAmount && watchedAmount > remainingAmount && (
              <p className="text-warning-600 text-sm mt-1 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                Investment exceeds remaining funding goal
              </p>
            )}
          </div>

          {/* Payment Method Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Method
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => {
                  setValue('paymentMethod', 'wallet');
                  setPreviewImage(null);
                  setSelectedFile(null);
                  setValue('paymentProof', undefined);
                }}
                className={`p-4 border-2 rounded-lg transition-colors ${
                  watchedPaymentMethod === 'wallet'
                    ? 'border-primary-500 bg-white'
                    : 'border-gray-300 hover:border-gray-400 bg-white'
                }`}
              >
                <Wallet className="h-6 w-6 mx-auto mb-2 text-primary-600" />
                <p className="font-semibold text-sm">Wallet Balance</p>
                <p className="text-xs text-gray-600 mt-1">
                  {formatCurrency(walletBalance, currencySymbol)} available
                </p>
              </button>
              <button
                type="button"
                onClick={() => setValue('paymentMethod', 'screenshot')}
                className={`p-4 border-2 rounded-lg transition-colors ${
                  watchedPaymentMethod === 'screenshot'
                    ? 'border-primary-500 bg-white'
                    : 'border-gray-300 hover:border-gray-400 bg-white'
                }`}
              >
                <Upload className="h-6 w-6 mx-auto mb-2 text-primary-600" />
                <p className="font-semibold text-sm">Upload Proof</p>
                <p className="text-xs text-gray-600 mt-1">Payment screenshot</p>
              </button>
            </div>
            {errors.paymentMethod && (
              <p className="text-danger-500 text-sm mt-1">{errors.paymentMethod.message}</p>
            )}
            {watchedPaymentMethod === 'wallet' && watchedAmount && walletBalance < watchedAmount && (
              <p className="text-warning-600 text-sm mt-2 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                Insufficient wallet balance. Please add funds or use payment screenshot method.
              </p>
            )}
          </div>

          {/* Expected Returns */}
          {watchedAmount && watchedAmount >= minInvestment && (
            <div className="bg-success-50 border border-success-200 rounded-lg p-4">
              <h4 className="font-semibold text-success-800 mb-2">Expected Returns</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-success-700">Investment Amount</p>
                  <p className="font-bold text-success-900">{formatCurrency(watchedAmount, currencySymbol)}</p>
                </div>
                <div>
                  <p className="text-success-700">Expected ROI</p>
                  <p className="font-bold text-success-900">
                    {formatCurrency(expectedROIAmount, currencySymbol)}
                  </p>
                </div>
              </div>
              <p className="text-xs text-success-600 mt-2">
                *Returns are projected based on {expectedROIPercentage}% ROI over {roiDuration} months
              </p>
            </div>
          )}

          {/* Payment Proof Upload - Only show when screenshot method is selected */}
          {watchedPaymentMethod === 'screenshot' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Proof
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-400 transition-colors">
              {previewImage ? (
                <div className="space-y-4">
                  <img
                    src={previewImage}
                    alt="Payment proof preview"
                    className="max-w-full h-48 object-contain mx-auto rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setPreviewImage(null);
                        setSelectedFile(null);
                      setValue('paymentProof', undefined);
                    }}
                    className="text-danger-600 hover:text-danger-800 text-sm"
                  >
                    Remove Image
                  </button>
                </div>
              ) : (
                <div>
                  <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-2">Upload payment screenshot or receipt</p>
                  <p className="text-xs text-gray-500 mb-4">PNG, JPG up to 10MB</p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                    id="payment-proof"
                  />
                  <label
                    htmlFor="payment-proof"
                    className="bg-white border-2 border-primary-600 text-primary-600 px-4 py-2 rounded-lg hover:bg-gray-50 cursor-pointer inline-block transition-colors"
                  >
                    Choose File
                  </label>
                </div>
              )}
            </div>
            {errors.paymentProof && (
              <p className="text-danger-500 text-sm mt-1">Payment proof is required</p>
            )}
          </div>
          )}

          {/* Terms */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-2">Important Notes</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Your investment will be reviewed within 24-48 hours</li>
              <li>• You will receive email confirmation once approved</li>
              <li>• Returns are projected and not guaranteed</li>
              <li>• Investment is subject to project terms and conditions</li>
            </ul>
          </div>

          {/* Submit Button */}
          <div className="flex space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                isLoading || 
                !watchedAmount || 
                watchedAmount < minInvestment || 
                !watchedPaymentMethod ||
                (watchedPaymentMethod === 'screenshot' && !selectedFile) ||
                (watchedPaymentMethod === 'wallet' && walletBalance < watchedAmount)
              }
              className="flex-1 py-3 px-4 bg-white border-2 border-primary-600 text-primary-600 rounded-lg hover:bg-gray-50 disabled:bg-gray-300 disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Submitting...' : watchedPaymentMethod === 'wallet' ? 'Invest from Wallet' : 'Submit Investment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InvestmentModal;